import { useTranslation } from "react-i18next";
import { SharingRights } from "@/shared/utils/constants";
import { Link, useNavigate } from "react-router-dom";

import {
  DataBlockDetailsProps,
  DataBlockContentProps,
} from "../../types/types";
import ContentItem from "@/components_v2/ContentElements/ContentItem";
import { DateElement } from "@/components_v2/ContentElements/DateElement";
import BaseTag from "@/components_v2/Tags/BaseTag";
import { getContentItemForField } from "@/domains/datablocks/fields/renderers";
import { DisplayFieldValue } from "@/domains/datablocks/types";
import { FieldValueResponse } from "@/redux/datablocks/types";

export const DataBlockDetails = ({
  dataBlock,
  dataTemplate,
}: DataBlockDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const fieldValues: DisplayFieldValue[] = [
    {
      field: dataTemplate.templateFields.find(
        (tf) => tf.id === dataBlock.fieldsMapping.publishDate,
      ),
      value: dataBlock.fieldValues.find(
        (fv) => fv.field === dataBlock.fieldsMapping.publishDate,
      )?.value,
    },
    {
      field: dataTemplate.templateFields.find(
        (tf) => tf.id === dataBlock.fieldsMapping.languages,
      ),
      value: dataBlock.fieldValues.find(
        (fv) => fv.field === dataBlock.fieldsMapping.languages,
      )?.value,
    },
  ].filter((fv): fv is DisplayFieldValue => !!fv.field);

  return (
    <div className="data-block-details">
      <h2 className="header-m-medium">
        {t("datablocks.labels.datablocksDetails")}
      </h2>

      <div className="data-block-details-content">
        {dataBlock.createdBy && (
          <ContentItem
            label={t("datablocks.labels.owner")}
            content={
              <Link to={`/users/${dataBlock.createdBy.id}`}>
                {dataBlock.createdBy.fullName}
              </Link>
            }
            showDivider={false}
            layout="horizontal"
          />
        )}

        <ContentItem
          label={t("datablocks.labels.organization")}
          content={
            <a
              onClick={() =>
                navigate(`/signatories/${dataBlock.organization.id}`)
              }
            >
              {dataBlock.organization.name}
            </a>
          }
          showDivider={false}
          layout="horizontal"
        />

        <ContentItem
          label={t("sharingRights.label")}
          content={
            <BaseTag label={SharingRights.getLabel(dataBlock.sharingRights)} />
          }
          showDivider={false}
          layout="horizontal"
        />

        {fieldValues.map((fieldValue) => getContentItemForField(fieldValue))}
        <ContentItem
          label={t("datablocks.labels.lastModified")}
          content={
            <DateElement value={dataBlock.updatedAt || dataBlock.createdAt} />
          }
          showDivider={false}
          layout="horizontal"
        />
      </div>
    </div>
  );
};

export const DataBlockContent = ({
  dataBlock,
  dataTemplate,
}: DataBlockContentProps) => {
  const { t } = useTranslation();

  const excludeFields = [
    dataBlock.fieldsMapping.languages,
    dataBlock.fieldsMapping.contributors,
    dataBlock.fieldsMapping.publishDate,
    dataBlock.fieldsMapping.title,
  ];

  const fieldValuesMap = dataBlock.fieldValues.reduce<
    Record<number, FieldValueResponse>
  >((acc, field) => {
    acc[field?.field] = field;
    return acc;
  }, {});

  const allFields: DisplayFieldValue[] = dataTemplate.templateFields
    .filter((field) => !excludeFields.includes(field.id))
    .sort((a, b) => a.order - b.order)
    .map((templateField) => {
      const fieldValue = fieldValuesMap[templateField.id];
      return {
        field: templateField,
        value: fieldValue?.value,
      };
    });

  return (
    <div className="data-block-content">
      <h2 className="header-m-medium">
        {t("datablocks.labels.datablocksContent")}
      </h2>

      <div className="data-block-content-items">
        {allFields.map((fieldValue) => getContentItemForField(fieldValue))}
      </div>
    </div>
  );
};
